import React, { Fragment, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ErrorBoundary } from '../../../common';
import { messages } from '../messages';
import { FunctionResult } from '../minions-output-items/function-result';
import { PluginOutputRenderer } from '../plugin-output-renderer';
import {
	type FunctionResultType,
	MinionName,
	type MinionOutput,
	type MinionPluginInvocationInfo,
	type ParsedPluginOutput,
} from '../types';
import { parsePluginOutput } from '../utils';

type ParsedMinionData = {
	extraInfo: MinionPluginInvocationInfo | string | null | undefined;
	parsedOutput: ParsedPluginOutput | null;
	functionResults: FunctionResultType[] | null;
};

type ParseMinionExtraInfoParams = {
	minionExtraInfo: string | undefined;
	minionId: string | undefined;
	isSearchMinion: boolean;
};

/**
 * Parses minion_extra_info to extract displayable data.
 *
 * The minion_extra_info can contain different structures depending on the minion type:
 *
 * 1. Search Minion with functionMessage:
 *    - Extracts pluginInvocationInfo.extraInfo containing searchResults/rankedDocuments
 *
 * 2. Non-Search Minion with functionMessage:
 *    - Extracts pluginInvocationInfo.output or functionMessage.output
 *    - Attempts to parse it using parsePluginOutput for structured display
 *
 * 3. Teamwork Agent with results array:
 *    - Extracts the results array containing function invocation results
 *    - Each result has pluginInvocationInfo with name, status, and output
 *
 * 4. Content fallback:
 *    - If none of the above, extracts the content field as plain text
 *
 * 5. Parse failure:
 *    - If JSON parsing fails, displays the raw minion_extra_info string
 */
const parseMinionExtraInfo = ({
	minionExtraInfo,
	minionId,
	isSearchMinion,
}: ParseMinionExtraInfoParams): ParsedMinionData => {
	let extraInfo: MinionPluginInvocationInfo | string | null = null;
	let parsedOutput: ParsedPluginOutput | null = null;
	let functionResults: FunctionResultType[] | null = null;

	if (!minionExtraInfo) {
		return { extraInfo, parsedOutput, functionResults };
	}

	try {
		const parsedExtraInfo = JSON.parse(minionExtraInfo);

		// Case 1 & 2: Has functionMessage (single function call result)
		if (parsedExtraInfo.functionMessage) {
			const { pluginInvocationInfo, output } = parsedExtraInfo.functionMessage;

			if (isSearchMinion && pluginInvocationInfo?.extraInfo) {
				// Search minion: extract nested search results from extraInfo
				try {
					extraInfo = JSON.parse(pluginInvocationInfo.extraInfo);
				} catch {
					extraInfo = pluginInvocationInfo.extraInfo;
				}
			} else {
				// Non-search minion: parse the output for structured display
				const rawOutput = pluginInvocationInfo?.output || output;
				if (typeof rawOutput === 'string') {
					parsedOutput = parsePluginOutput(minionId, rawOutput);
					if (!parsedOutput) {
						extraInfo = rawOutput;
					}
				}
			}
		}
		// Case 3: Has results array (multiple function calls, e.g., Teamwork Agent)
		else if (Array.isArray(parsedExtraInfo.results) && parsedExtraInfo.results.length > 0) {
			functionResults = parsedExtraInfo.results as FunctionResultType[];
		}
		// Case 4: Has content field (plain text fallback)
		else if (parsedExtraInfo.content) {
			extraInfo = parsedExtraInfo.content;
		}
	} catch {
		// Case 5: JSON parsing failed, use raw string
		extraInfo = minionExtraInfo;
	}

	return { extraInfo, parsedOutput, functionResults };
};

const minionStyles = cssMap({
	pressable: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		cursor: 'pointer',
		width: '100%',
	},
	content: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
});

export const MinionInvocationItem = ({
	minion,
	index,
}: {
	minion: MinionOutput;
	index: number;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const minionInfo = Array.isArray(minion) ? minion[0] : minion;
	const isSearchMinion = minionInfo.minion_id?.toLowerCase().includes(MinionName.SEARCH);

	const { extraInfo, parsedOutput, functionResults } = parseMinionExtraInfo({
		minionExtraInfo: minionInfo.minion_extra_info,
		minionId: minionInfo.minion_id,
		isSearchMinion,
	});

	return (
		<ErrorBoundary fallback={<Fragment />}>
			<Box>
				<Pressable onClick={() => setIsExpanded(!isExpanded)} xcss={minionStyles.pressable}>
					<Inline space="space.100" alignBlock="center">
						<IconButton
							icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
							label={formatMessage(
								isExpanded
									? messages.ai_debugger_modal_collapse_label
									: messages.ai_debugger_modal_expand_label,
							)}
							appearance="subtle"
							spacing="compact"
						/>
						<Text weight="medium">{minionInfo.minion_id || `Minion ${index + 1}`}</Text>
					</Inline>
				</Pressable>
				{isExpanded && (
					<Box xcss={minionStyles.content}>
						<Stack space="space.200">
							{functionResults && functionResults.length > 0 ? (
								<Stack space="space.100">
									<Text weight="medium" size="small">
										{formatMessage(messages.ai_debugger_modal_function_results_count, {
											count: functionResults.length,
										})}
									</Text>
									{functionResults.map((result, idx) => (
										<FunctionResult key={idx} result={result} index={idx} />
									))}
								</Stack>
							) : (
								<PluginOutputRenderer parsedOutput={parsedOutput} extraInfo={extraInfo} />
							)}
						</Stack>
					</Box>
				)}
			</Box>
		</ErrorBoundary>
	);
};
